/**
 * ثبتِ انتخاب‌های کاربر روی سرور — یک‌طرفه، و فقط وقتی چیزی عوض می‌شود.
 *
 * ═══ این «سینکِ تنظیمات» نیست ═══
 *
 * هیچ‌چیز از این‌جا خوانده و به کاربر تحمیل نمی‌شود. مرجعِ هر تنظیم همان
 * حافظه‌ی دستگاه است که همیشه بود، و رفتارِ امروزِ تم و نمای فهرست و شهرِ
 * درحالِ‌تماشا هیچ عوض نمی‌شود. این‌جا فقط رونوشتی نگه داشته می‌شود که به
 * سوالِ «آدم‌ها با اپ چه می‌کنند؟» جواب بدهد (مایگریشن ۰۱۱۱).
 *
 * ⚠ تنها استثنا `locale` است: تریگرهای اعلان می‌خوانندش تا بدانند متن را
 * به چه زبانی بسازند (مایگریشن ۰۱۰۹). پس آن یکی علاوه بر ثبت، روی خودِ
 * `profiles.locale` هم نوشته می‌شود.
 *
 * ═══ چرا شناسه‌ی کاربر تزریق می‌شود و پرسیده نمی‌شود ═══
 *
 * همان الگوی `setDevLogUser` در `lib/devlog.ts`: `SessionProvider` بعد از
 * رسیدنِ پروفایل شناسه را می‌دهد. جایگزینش `auth.getUser()` در هر ثبت بود —
 * یک رفت‌وبرگشتِ شبکه برای یادداشتی که خودش قرار است بی‌صدا باشد.
 */
import { supabase } from '../lib/supabase';
import { platformInfo } from '../platform';
import { isDemoMode } from '../lib/demo';
import { deviceId } from '../lib/deviceId';
import { setPrefSink, type PrefKey } from '../lib/prefSink';
import { pickedLocale } from '../lib/i18n';
import type { Profile } from '../domain/types';

let userId: string | null = null;

/** اجازه‌ی اعلان از نگاهِ این دستگاه — همان مقادیرِ ستونِ `push_permission`. */
export type PushPermission = 'unknown' | 'default' | 'granted' | 'denied' | 'snoozed';

/**
 * ثبت روی سرور. هیچ‌وقت throw نمی‌کند و هیچ‌وقت await نمی‌شود.
 *
 * کاربرِ واردنشده ثبت نمی‌شود و بافر هم نمی‌شود: سطر بدونِ صاحب معنایی
 * ندارد و RLS هم قبولش نمی‌کند. تنها چیزی که این‌طوری از دست می‌رود
 * انتخاب‌های قبل از ساختنِ حساب است — که `syncLocale` برای مهم‌ترینشان
 * (زبان) جبرانش می‌کند.
 */
async function write(key: PrefKey, value: unknown): Promise<void> {
  if (isDemoMode || !userId) return;
  const device = await deviceId();
  const { error } = await supabase.from('user_pref_events').insert({
    user_id: userId, device_id: device, key, value,
  });
  if (error) throw error;
}

export const prefLogRepo = {
  /** `SessionProvider` بعد از رسیدنِ پروفایل صدا می‌زند (و با null سرِ خروج). */
  setUser(id: string | null): void { userId = id; },

  /**
   * قلابِ `lib/prefSink` را می‌نشاند. اپ‌ها سرِ بوت یک‌بار صدایش می‌زنند.
   *
   * از این لحظه هر `setTheme`/`setPref`/`setLocale`/… خودش ثبت می‌شود و
   * هیچ صفحه‌ای لازم نیست کاری بکند — همان الگوی `tapped()` در دو کیتِ UI.
   */
  install(): void {
    setPrefSink((key, value) => {
      void write(key, value).catch(() => undefined);
      if (key === 'locale' && typeof value === 'string') {
        void prefLogRepo.writeLocale(value).catch(() => undefined);
      }
    });
  },

  /**
   * `profiles.locale` — تنها تنظیمی که سرور واقعاً **می‌خواندش**.
   *
   * بدونِ این، تریگرهای اعلان نمی‌دانند متن را به چه زبانی بسازند و به
   * انگلیسی می‌افتند.
   */
  async writeLocale(locale: string): Promise<void> {
    if (isDemoMode || !userId) return;
    const { error } = await supabase
      .from('profiles').update({ locale }).eq('id', userId);
    if (error) throw error;
  },

  /**
   * آشتیِ یک‌باره‌ی زبان، سرِ رسیدنِ پروفایل.
   *
   * ⚠ این تنها جایی است که چیزی سرِ **بوت** نوشته می‌شود، و عمدی است. دو
   * شکافی را می‌بندد که وگرنه باز می‌ماندند:
   *
   *   ۱. کسی که زبانش را قبل از ساختنِ حساب انتخاب کرده — آن لحظه صاحبی
   *      نبود که سطر به نامش بخورد.
   *   ۲. کسی که زبانش را قبل از وجود داشتنِ این قابلیت انتخاب کرده — یعنی
   *      همه‌ی حساب‌های امروز، که مایگریشنِ ۰۱۰۹ `locale`شان را به null
   *      برگرداند.
   *
   * جریان همچنان یک‌طرفه است: مقدارِ سرور خوانده می‌شود ولی هیچ‌وقت روی
   * انتخابِ کاربر نمی‌نشیند — فقط مقایسه می‌شود تا بی‌دلیل ننویسیم.
   *
   * و فقط انتخابِ **صریح**: `pickedLocale()` وقتی null می‌دهد که کاربر
   * هیچ‌وقت زبانی نگزیده و اپ از روی زبانِ گوشی حدس زده. حدس را نباید
   * به‌عنوان تصمیم روی حساب نوشت.
   */
  async syncLocale(profile: Profile | null): Promise<void> {
    if (isDemoMode || !profile || !userId) return;
    const picked = await pickedLocale();
    if (!picked || picked === profile.locale) return;
    await prefLogRepo.writeLocale(picked);
    await write('locale', picked).catch(() => undefined);
  },

  /**
   * «این دستگاه را دیدم.»
   *
   * ⚠ تنها چیزی که در هر بار بالا آمدنِ اپ نوشته می‌شود، و استثنای دومِ
   * قاعده‌ی «فقط موقعِ تغییر». دلیلش این است که خودِ سوال بدونش جواب ندارد:
   * «آخرین مرورگرش کدام بود» یعنی `last_seen`، و `last_seen` فقط با دیدن
   * به‌روز می‌شود.
   *
   * مشخصاتِ دستگاه از پورتِ `platform` می‌آید، نه از اپ: هر دو اپ سرِ بوت
   * پرش می‌کنند (`apps/web/src/boot.ts`، `apps/mobile/src/native/bootstrap.ts`)
   * و core نباید دو بار همان چیز را از دو راه بگیرد.
   *
   * `touch_device` (تابعِ ۰۱۱۱) هم upsert می‌کند، هم `first_seen` را حفظ
   * می‌کند، و ساعت را از سرور می‌گیرد نه از گوشی که می‌تواند روزها پرت باشد.
   */
  async touchDevice(permission?: PushPermission): Promise<void> {
    if (isDemoMode || !userId) return;
    const device = await deviceId();
    if (!device) return;
    const p = platformInfo();
    const { error } = await supabase.rpc('touch_device', {
      p_device_id: device,
      p_surface: p.isWeb ? 'web' : 'mobile',
      p_platform: p.os,
      // روی وب `osVersion` همان برچسبِ کوتاهِ مرورگر است («Chrome 141») —
      // نگاه کن به `browserLabel()` در boot.ts. روی موبایل نسخه‌ی اندروید.
      p_os_version: p.osVersion || null,
      p_app_version: p.appVersion,
      p_browser: p.isWeb ? (p.osVersion || null) : null,
      p_permission: permission ?? null,
    });
    if (error) throw error;
  },

  /**
   * اجازه‌ی اعلانِ این دستگاه عوض شد.
   *
   * هم روی سطرِ دستگاه می‌نشیند (برای «آخری اجازه داده یا نه») و هم در
   * تاریخچه (برای «کِی و چند بار نظرش عوض شد») — دو سوالِ متفاوت که یک
   * جدولِ تنها هر دو را جواب نمی‌دهد.
   */
  async setPushPermission(permission: PushPermission): Promise<void> {
    if (isDemoMode || !userId) return;
    await prefLogRepo.touchDevice(permission);
    await write('pushPermission', permission);
  },

  /**
   * آیا این حساب تا حالا یک بار این تنظیم را ثبت کرده؟ — فعلاً فقط برای تور:
   * کسی که تور را روی گوشیِ دیگری (یا با «آماده‌سازیِ محیط» ِ صفحه‌ی تست،
   * مایگریشن 0184) دیده، روی نصبِ تازه دوباره نمی‌بیندش.
   *
   * ⚠ این تنها **خواندنِ** این جدول است. خطا یا حالتِ دمو = «نه»، یعنی همان
   * رفتارِ قبلی (حافظه‌ی دستگاه تصمیم می‌گیرد).
   */
  async hasRecorded(user: string, key: PrefKey): Promise<boolean> {
    if (isDemoMode || !user) return false;
    try {
      const { data, error } = await supabase.from('user_pref_events')
        .select('id').eq('user_id', user).eq('key', key).limit(1);
      return !error && (data?.length ?? 0) > 0;
    } catch {
      return false;
    }
  },
};
